import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";

/* ═══════════════════════════════════════════════════════════════
   usePostnatalMaternal — Custom hook for Postnatal Care – Maternal (Task 15)
   Manages PNC visit scheduling (4 contacts), maternal assessments,
   Edinburgh Postnatal Depression Scale, danger-sign alerts, and
   links to delivery record + ANC booking.
   ═══════════════════════════════════════════════════════════════ */

const USE_MOCK_DATA = false;

/* ─── Constants ─── */

export const PNC_VISIT_LABELS = [
    { number: 1, label: "PNC 1 — Within 24 hours", timing: "Within 24 hours", hoursAfter: 24 },
    { number: 2, label: "PNC 2 — Day 3", timing: "48–72 hours (Day 3)", hoursAfter: 72 },
    { number: 3, label: "PNC 3 — Day 7", timing: "Day 7–14", hoursAfter: 168 },
    { number: 4, label: "PNC 4 — 6 Weeks", timing: "6 weeks postpartum", hoursAfter: 1008 },
];

export const UTERINE_INVOLUTION = [
    { value: "well_contracted", label: "Well Contracted" },
    { value: "poorly_contracted", label: "Poorly Contracted" },
    { value: "not_palpable", label: "Not Palpable (involuted)" },
];

export const LOCHIA_STATUS = [
    { value: "normal", label: "Normal (lochia rubra/serosa/alba)" },
    { value: "offensive", label: "Offensive / Foul-Smelling" },
    { value: "heavy", label: "Heavy / Excessive" },
];

export const PERINEUM_STATUS = [
    { value: "healing", label: "Healing Well" },
    { value: "infected", label: "Infected" },
    { value: "dehisced", label: "Dehisced (wound breakdown)" },
    { value: "na", label: "N/A — No wound" },
];

export const BREAST_STATUS = [
    { value: "normal", label: "Normal" },
    { value: "engorged", label: "Engorged" },
    { value: "mastitis", label: "Mastitis" },
    { value: "abscess", label: "Breast Abscess" },
];

export const BREASTFEEDING_STATUS = [
    { value: "exclusive", label: "Exclusive Breastfeeding" },
    { value: "mixed", label: "Mixed Feeding" },
    { value: "not_breastfeeding", label: "Not Breastfeeding" },
];

export const PNC_COMPLICATION_OPTIONS = [
    { value: "pph_secondary", label: "Secondary PPH" },
    { value: "puerperal_sepsis", label: "Puerperal Sepsis" },
    { value: "wound_infection", label: "Wound Infection (perineum/CS)" },
    { value: "dvt", label: "Deep Vein Thrombosis (DVT)" },
    { value: "pulmonary_embolism", label: "Pulmonary Embolism" },
    { value: "eclampsia_postpartum", label: "Postpartum Eclampsia" },
    { value: "mastitis", label: "Mastitis / Breast Abscess" },
    { value: "anaemia", label: "Postpartum Anaemia" },
    { value: "urinary_retention", label: "Urinary Retention" },
    { value: "fistula", label: "Obstetric Fistula (VVF/RVF)" },
    { value: "depression", label: "Postnatal Depression" },
    { value: "psychosis", label: "Puerperal Psychosis" },
    { value: "none", label: "No Complications" },
];

/* ─── Edinburgh Postnatal Depression Scale (EPDS) Questions ─── */
export const EPDS_QUESTIONS = [
    {
        id: 1,
        text: "I have been able to laugh and see the funny side of things",
        options: [
            { value: 0, label: "As much as I always could" },
            { value: 1, label: "Not quite so much now" },
            { value: 2, label: "Definitely not so much now" },
            { value: 3, label: "Not at all" },
        ],
    },
    {
        id: 2,
        text: "I have looked forward with enjoyment to things",
        options: [
            { value: 0, label: "As much as I ever did" },
            { value: 1, label: "Rather less than I used to" },
            { value: 2, label: "Definitely less than I used to" },
            { value: 3, label: "Hardly at all" },
        ],
    },
    {
        id: 3,
        text: "I have blamed myself unnecessarily when things went wrong",
        options: [
            { value: 3, label: "Yes, most of the time" },
            { value: 2, label: "Yes, some of the time" },
            { value: 1, label: "Not very often" },
            { value: 0, label: "No, never" },
        ],
    },
    {
        id: 4,
        text: "I have been anxious or worried for no good reason",
        options: [
            { value: 0, label: "No, not at all" },
            { value: 1, label: "Hardly ever" },
            { value: 2, label: "Yes, sometimes" },
            { value: 3, label: "Yes, very often" },
        ],
    },
    {
        id: 5,
        text: "I have felt scared or panicky for no very good reason",
        options: [
            { value: 3, label: "Yes, quite a lot" },
            { value: 2, label: "Yes, sometimes" },
            { value: 1, label: "No, not much" },
            { value: 0, label: "No, not at all" },
        ],
    },
    {
        id: 6,
        text: "Things have been getting on top of me",
        options: [
            { value: 3, label: "Yes, most of the time I haven't been able to cope at all" },
            { value: 2, label: "Yes, sometimes I haven't been coping as well as usual" },
            { value: 1, label: "No, most of the time I have coped quite well" },
            { value: 0, label: "No, I have been coping as well as ever" },
        ],
    },
    {
        id: 7,
        text: "I have been so unhappy that I have had difficulty sleeping",
        options: [
            { value: 3, label: "Yes, most of the time" },
            { value: 2, label: "Yes, sometimes" },
            { value: 1, label: "Not very often" },
            { value: 0, label: "No, not at all" },
        ],
    },
    {
        id: 8,
        text: "I have felt sad or miserable",
        options: [
            { value: 3, label: "Yes, most of the time" },
            { value: 2, label: "Yes, quite often" },
            { value: 1, label: "Not very often" },
            { value: 0, label: "No, not at all" },
        ],
    },
    {
        id: 9,
        text: "I have been so unhappy that I have been crying",
        options: [
            { value: 3, label: "Yes, most of the time" },
            { value: 2, label: "Yes, quite often" },
            { value: 1, label: "Only occasionally" },
            { value: 0, label: "No, never" },
        ],
    },
    {
        id: 10,
        text: "The thought of harming myself has occurred to me",
        options: [
            { value: 3, label: "Yes, quite often" },
            { value: 2, label: "Sometimes" },
            { value: 1, label: "Hardly ever" },
            { value: 0, label: "Never" },
        ],
        isCritical: true,
    },
];

/* ─── Helper ─── */
const nowLocal = function () {
    const d = new Date();
    const pad = function (n) { return n < 10 ? "0" + n : "" + n; };
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) +
        "T" + pad(d.getHours()) + ":" + pad(d.getMinutes());
};

const todayDate = function () {
    const d = new Date();
    const pad = function (n) { return n < 10 ? "0" + n : "" + n; };
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
};

/* ─── Initial Maternal Form ─── */
const INITIAL_MATERNAL_FORM = {
    pnc_visit_number: "",
    visit_date: todayDate(),
    bp_systolic: "",
    bp_diastolic: "",
    pulse: "",
    temperature: "",
    uterine_involution: "",
    lochia: "",
    perineum_wound: "na",
    breasts: "",
    breastfeeding_status: "",
    hb_if_checked: "",
    depression_screening_score: null,
    depression_screening_tool: "edinburgh_postnatal",
    dvt_signs: false,
    complications: [],
    iron_folate_given: false,
    vitamin_a_given: false,
    fp_method_started: "",
    art_continuation: false,
    cervical_screening_referral: false,
    notes: "",
    next_visit_date: "",
};

/* ═══ MOCK DATA ═══ */

const MOCK_DELIVERY = {
    id: 8001,
    labour_admission_id: 9001,
    booking_id: 3001,
    delivery_datetime: "2026-02-28T14:30",
    delivery_mode: "svd",
    estimated_blood_loss_ml: 350,
    maternal_condition_post_delivery: "stable",
    number_of_babies: 1,
    complications: [],
};

const MOCK_BOOKING = {
    id: 3001,
    patient_number: "MOCK-PT-001",
    lnmp: "2025-06-01",
    edd: "2026-03-08",
    gravida: 2,
    parity: 1,
    blood_group: "O+",
    status: "delivered",
};

const MOCK_PNC_VISITS = [
    {
        id: 101,
        delivery_id: 8001,
        pnc_visit_number: 1,
        visit_date: "2026-02-28",
        bp_systolic: 120,
        bp_diastolic: 75,
        pulse: 78,
        temperature: 36.8,
        uterine_involution: "well_contracted",
        lochia: "normal",
        perineum_wound: "healing",
        breasts: "normal",
        breastfeeding_status: "exclusive",
        hb_if_checked: "",
        depression_screening_score: null,
        dvt_signs: false,
        complications: [],
        iron_folate_given: true,
        vitamin_a_given: true,
        art_continuation: false,
        cervical_screening_referral: false,
        notes: "Mother and baby doing well. Counselled on exclusive breastfeeding.",
        next_visit_date: "2026-03-02",
        seen_by: "Dr. Sarah Nakato",
        created_at: "2026-02-28T15:00:00",
    },
];

/* ─── Schedule Generation ─── */
const generatePNCSchedule = function (deliveryDateStr) {
    if (!deliveryDateStr) return [];
    const d = new Date(deliveryDateStr);
    return PNC_VISIT_LABELS.map(function (v) {
        const scheduled = new Date(d.getTime() + v.hoursAfter * 60 * 60 * 1000);
        const pad = function (n) { return n < 10 ? "0" + n : "" + n; };
        return {
            number: v.number,
            label: v.label,
            timing: v.timing,
            scheduled_date: scheduled.getFullYear() + "-" + pad(scheduled.getMonth() + 1) + "-" + pad(scheduled.getDate()),
        };
    });
};

/* ═══ HOOK ═══ */
export const usePostnatalMaternal = function (actions, patient_number, visit_id, onSaveSuccess) {

    /* ─── Delivery record ─── */
    const delState = useState(null);
    const deliveryRecord = delState[0];
    const setDeliveryRecord = delState[1];

    /* ─── Booking ─── */
    const bookState = useState(null);
    const booking = bookState[0];
    const setBooking = bookState[1];

    /* ─── PNC visits ─── */
    const visitsState = useState([]);
    const pncVisits = visitsState[0];
    const setPncVisits = visitsState[1];

    /* ─── Schedule ─── */
    const schedState = useState([]);
    const pncSchedule = schedState[0];
    const setPncSchedule = schedState[1];

    /* ─── Form state ─── */
    const formState = useState(Object.assign({}, INITIAL_MATERNAL_FORM));
    const form = formState[0];
    const setForm = formState[1];

    /* ─── EPDS responses ─── */
    const epdsState = useState({});
    const epdsResponses = epdsState[0];
    const setEpdsResponses = epdsState[1];

    /* ─── Editing visit ─── */
    const editState = useState(null);
    const editingVisit = editState[0];
    const setEditingVisit = editState[1];

    /* ─── UI state ─── */
    const loadState = useState(true);
    const loading = loadState[0];
    const setLoading = loadState[1];

    const savingState = useState(false);
    const saving = savingState[0];
    const setSaving = savingState[1];

    const tabState = useState("assessment");
    const activeTab = tabState[0];
    const setActiveTab = tabState[1];

    const showEpdsState = useState(false);
    const showEpds = showEpdsState[0];
    const setShowEpds = showEpdsState[1];

    /* ─── Fetch data ─── */
    useEffect(function () {
        if (!patient_number || !visit_id) return;
        setLoading(true);
        if (USE_MOCK_DATA) {
            setTimeout(function () {
                setDeliveryRecord(MOCK_DELIVERY);
                setBooking(MOCK_BOOKING);
                setPncVisits(MOCK_PNC_VISITS);
                const sched = generatePNCSchedule(MOCK_DELIVERY.delivery_datetime);
                setPncSchedule(sched);
                /* Set default next visit number */
                const nextNum = MOCK_PNC_VISITS.length + 1;
                if (nextNum <= 4) {
                    setForm(function (prev) {
                        return Object.assign({}, prev, { pnc_visit_number: String(nextNum) });
                    });
                }
                setLoading(false);
            }, 400);
            return;
        }
        Promise.all([
            axios.get(config.smsUrl + "/obgyn/delivery/by-patient/" + patient_number),
            axios.get(config.smsUrl + "/obgyn/anc/booking/patient/" + patient_number),
        ]).then(function (responses) {
            const delData = responses[0].data || {};
            const bookData = responses[1].data || {};
            if (delData.id) {
                setDeliveryRecord(delData);
                const sched = generatePNCSchedule(delData.delivery_datetime);
                setPncSchedule(sched);
            }
            if (bookData.id) setBooking(bookData);
            if (delData.id) {
                return axios.get(config.smsUrl + "/obgyn/pnc/maternal/" + delData.id);
            }
            return null;
        }).then(function (res) {
            if (res && res.data) {
                const visits = Array.isArray(res.data) ? res.data : (res.data.visits || []);
                setPncVisits(visits);
                const nextNum = visits.length + 1;
                if (nextNum <= 4) {
                    setForm(function (prev) {
                        return Object.assign({}, prev, { pnc_visit_number: String(nextNum) });
                    });
                }
            }
        }).catch(function () { }).finally(function () { setLoading(false); });
    }, [patient_number, visit_id]);

    /* ─── Form change handler ─── */
    const handleChange = useCallback(function (e) {
        const name = e.target.name;
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm(function (prev) {
            const next = Object.assign({}, prev);
            next[name] = value;
            return next;
        });
    }, []);

    /* ─── Complication toggle ─── */
    const toggleComplication = useCallback(function (compValue) {
        setForm(function (prev) {
            const current = prev.complications || [];
            const idx = current.indexOf(compValue);
            let next;
            if (idx >= 0) {
                next = current.filter(function (c) { return c !== compValue; });
            } else {
                next = current.concat([compValue]);
            }
            return Object.assign({}, prev, { complications: next });
        });
    }, []);

    /* ─── EPDS response handler ─── */
    const handleEpdsResponse = useCallback(function (questionId, value) {
        setEpdsResponses(function (prev) {
            const next = Object.assign({}, prev);
            next[questionId] = value;
            return next;
        });
    }, []);

    /* ─── EPDS score computation ─── */
    const epdsScore = useMemo(function () {
        const keys = Object.keys(epdsResponses);
        if (keys.length === 0) return null;
        let total = 0;
        keys.forEach(function (k) { total += epdsResponses[k]; });
        return total;
    }, [epdsResponses]);

    const epdsComplete = useMemo(function () {
        return Object.keys(epdsResponses).length === 10;
    }, [epdsResponses]);

    const epdsInterpretation = useMemo(function () {
        if (epdsScore === null) return null;
        if (epdsScore <= 9) return { level: "low", label: "Low risk", color: "#2e7d32", detail: "Score ≤ 9: Depression unlikely. Continue routine follow-up." };
        if (epdsScore <= 12) return { level: "moderate", label: "Possible depression", color: "#f57f17", detail: "Score 10–12: Possible depression. Repeat in 2 weeks. Consider counselling." };
        if (epdsScore <= 30) return { level: "high", label: "Probable depression", color: "#c62828", detail: "Score ≥ 13: Probable depression. Refer for mental health assessment. Consider treatment." };
        return null;
    }, [epdsScore]);

    /* ─── Apply EPDS to form ─── */
    const applyEpdsScore = useCallback(function () {
        if (epdsScore !== null) {
            setForm(function (prev) {
                return Object.assign({}, prev, { depression_screening_score: epdsScore });
            });
            setShowEpds(false);
        }
    }, [epdsScore]);

    /* ─── Vital sign alerts ─── */
    const alerts = useMemo(function () {
        const list = [];

        /* Blood pressure */
        const sys = parseFloat(form.bp_systolic);
        const dia = parseFloat(form.bp_diastolic);
        if (sys >= 160 || dia >= 110) {
            list.push({
                type: "critical",
                title: "Severe Hypertension",
                detail: "BP " + sys + "/" + dia + " mmHg — Risk of postpartum eclampsia",
                action: "URGENT: Administer antihypertensives. MgSO4 if signs of eclampsia. Monitor closely.",
            });
        } else if (sys >= 140 || dia >= 90) {
            list.push({
                type: "warning",
                title: "Elevated Blood Pressure",
                detail: "BP " + sys + "/" + dia + " mmHg",
                action: "Monitor 4-hourly. Consider antihypertensive if persists. Check urine protein.",
            });
        }

        /* Temperature — sepsis */
        const temp = parseFloat(form.temperature);
        if (temp >= 38.5) {
            list.push({
                type: "critical",
                title: "Fever — Possible Puerperal Sepsis",
                detail: "Temperature " + temp + "°C",
                action: "Take blood cultures. Start broad-spectrum antibiotics. Monitor uterine tenderness.",
            });
        } else if (temp >= 37.5) {
            list.push({
                type: "warning",
                title: "Low-Grade Fever",
                detail: "Temperature " + temp + "°C",
                action: "Investigate cause: UTI, wound infection, mastitis, endometritis.",
            });
        }

        /* Offensive lochia */
        if (form.lochia === "offensive") {
            list.push({
                type: "critical",
                title: "Offensive Lochia — Endometritis Risk",
                detail: "Foul-smelling lochia may indicate puerperal sepsis",
                action: "Examine uterus. Start antibiotics (ampicillin + gentamicin + metronidazole).",
            });
        }

        /* Heavy lochia */
        if (form.lochia === "heavy") {
            list.push({
                type: "warning",
                title: "Heavy Lochia — Secondary PPH Risk",
                detail: "Excessive vaginal bleeding in postnatal period",
                action: "Check Hb. Uterotonic if needed. Consider ultrasound for retained products.",
            });
        }

        /* Poorly contracted uterus */
        if (form.uterine_involution === "poorly_contracted") {
            list.push({
                type: "warning",
                title: "Poorly Contracted Uterus",
                detail: "Uterus not involuting as expected",
                action: "Uterine massage. Check for retained products. Consider uterotonics.",
            });
        }

        /* DVT */
        if (form.dvt_signs) {
            list.push({
                type: "critical",
                title: "DVT Signs Detected",
                detail: "Clinical signs of deep vein thrombosis present",
                action: "URGENT: Doppler ultrasound. Start anticoagulation. Do NOT massage limb.",
            });
        }

        /* Wound infection/dehiscence */
        if (form.perineum_wound === "infected") {
            list.push({
                type: "warning",
                title: "Perineal Wound Infection",
                detail: "Signs of infection at perineal wound/episiotomy site",
                action: "Wound care. Antibiotics. Follow up in 48 hours.",
            });
        }
        if (form.perineum_wound === "dehisced") {
            list.push({
                type: "critical",
                title: "Wound Dehiscence",
                detail: "Perineal wound has broken down",
                action: "Surgical review for secondary repair. Wound care. Antibiotics.",
            });
        }

        /* Breast abscess */
        if (form.breasts === "abscess") {
            list.push({
                type: "warning",
                title: "Breast Abscess",
                detail: "Breast abscess detected — may require incision and drainage",
                action: "Antibiotics. Consider I&D. Continue breastfeeding from unaffected side.",
            });
        }

        /* EPDS */
        if (form.depression_screening_score !== null && form.depression_screening_score >= 13) {
            list.push({
                type: "critical",
                title: "High EPDS Score — Probable Depression",
                detail: "Edinburgh Postnatal Depression Scale score: " + form.depression_screening_score + "/30",
                action: "REFER for mental health assessment. Assess safety (question 10). Consider antidepressant.",
            });
        } else if (form.depression_screening_score !== null && form.depression_screening_score >= 10) {
            list.push({
                type: "warning",
                title: "Moderate EPDS Score — Possible Depression",
                detail: "Edinburgh Postnatal Depression Scale score: " + form.depression_screening_score + "/30",
                action: "Repeat screening in 2 weeks. Offer counselling support.",
            });
        }

        return list;
    }, [form]);

    /* ─── Schedule with status ─── */
    const scheduleWithStatus = useMemo(function () {
        return pncSchedule.map(function (s) {
            const matchingVisit = pncVisits.find(function (v) {
                return v.pnc_visit_number === s.number;
            });
            const today = new Date();
            const schedDate = new Date(s.scheduled_date);
            let status = "future";
            if (matchingVisit) {
                status = "attended";
            } else if (schedDate < today) {
                status = "missed";
            } else {
                /* Check if within 24h of scheduled */
                const diffMs = schedDate.getTime() - today.getTime();
                const diffDays = diffMs / (1000 * 60 * 60 * 24);
                if (diffDays <= 1 && diffDays >= -1) {
                    status = "due";
                } else if (diffDays > 0) {
                    status = "upcoming";
                }
            }
            return Object.assign({}, s, { status: status, visit: matchingVisit || null });
        });
    }, [pncSchedule, pncVisits]);

    /* ─── Days postpartum ─── */
    const daysPostpartum = useMemo(function () {
        if (!deliveryRecord || !deliveryRecord.delivery_datetime) return null;
        const delDate = new Date(deliveryRecord.delivery_datetime);
        const now = new Date();
        const diffMs = now.getTime() - delDate.getTime();
        return Math.floor(diffMs / (1000 * 60 * 60 * 24));
    }, [deliveryRecord]);

    /* ─── Edit an existing visit ─── */
    const startEditVisit = useCallback(function (visit) {
        setEditingVisit(visit);
        setForm(Object.assign({}, INITIAL_MATERNAL_FORM, visit, {
            pnc_visit_number: String(visit.pnc_visit_number),
        }));
        if (visit.depression_screening_score !== null && visit.depression_screening_score !== undefined) {
            /* We don't restore individual EPDS answers since they aren't stored; just the score */
        }
        setActiveTab("assessment");
    }, []);

    /* ─── Reset form for new visit ─── */
    const resetForm = useCallback(function () {
        setEditingVisit(null);
        const nextNum = pncVisits.length + 1;
        setForm(Object.assign({}, INITIAL_MATERNAL_FORM, {
            pnc_visit_number: nextNum <= 4 ? String(nextNum) : "",
        }));
        setEpdsResponses({});
    }, [pncVisits]);

    /* ─── Save PNC maternal visit ─── */
    const savePncVisit = useCallback(function () {
        if (!form.pnc_visit_number) {
            actions.snackbarActions.snackbarOpen("error", "Please select a PNC visit number");
            return;
        }
        if (!form.visit_date) {
            actions.snackbarActions.snackbarOpen("error", "Please enter the visit date");
            return;
        }
        setSaving(true);
        const payload = Object.assign({}, form, {
            delivery_id: deliveryRecord ? deliveryRecord.id : null,
            booking_id: booking ? booking.id : null,
            visit_id: visit_id,
            pnc_visit_number: parseInt(form.pnc_visit_number, 10),
            depression_screening_score: epdsScore !== null ? epdsScore : form.depression_screening_score,
        });

        if (USE_MOCK_DATA) {
            setTimeout(function () {
                if (editingVisit) {
                    const updated = Object.assign({}, editingVisit, payload);
                    const updatedVisits = pncVisits.map(function (v) {
                        return v.id === editingVisit.id ? updated : v;
                    });
                    setPncVisits(updatedVisits);
                    actions.snackbarActions.snackbarOpen("success", "PNC visit updated (mock)");
                } else {
                    const saved = Object.assign({}, payload, {
                        id: Date.now(),
                        seen_by: "Current User",
                        created_at: new Date().toISOString(),
                    });
                    setPncVisits(function (prev) { return prev.concat([saved]); });
                    actions.snackbarActions.snackbarOpen("success", "PNC visit saved (mock)");
                }
                resetForm();
                setSaving(false);
                if (typeof onSaveSuccess === "function") onSaveSuccess();
            }, 300);
            return;
        }

        const request = editingVisit
            ? axios.put(config.smsUrl + "/obgyn/pnc/maternal/" + editingVisit.id, payload)
            : axios.post(config.smsUrl + "/obgyn/pnc/maternal", payload);

        request
            .then(function (res) {
                const data = res.data || payload;
                if (editingVisit) {
                    setPncVisits(function (prev) {
                        return prev.map(function (v) { return v.id === editingVisit.id ? data : v; });
                    });
                } else {
                    setPncVisits(function (prev) { return prev.concat([data]); });
                }
                actions.snackbarActions.snackbarOpen("success",
                    editingVisit ? "PNC visit updated" : "PNC visit saved");
                resetForm();
                if (typeof onSaveSuccess === "function") onSaveSuccess();
            })
            .catch(function (err) {
                const msg = (err.response && err.response.data && err.response.data.message) ||
                    "Failed to save PNC visit";
                actions.snackbarActions.snackbarOpen("error", msg);
            })
            .finally(function () { setSaving(false); });
    }, [form, deliveryRecord, booking, visit_id, epdsScore, editingVisit, pncVisits, actions, resetForm, onSaveSuccess]);

    /* ─── Summary ─── */
    const summary = useMemo(function () {
        return {
            totalVisits: pncVisits.length,
            completedVisits: pncVisits.length,
            remainingVisits: 4 - pncVisits.length,
            latestVitals: pncVisits.length > 0 ? pncVisits[pncVisits.length - 1] : null,
            hasComplications: pncVisits.some(function (v) { return v.complications && v.complications.length > 0 && v.complications[0] !== "none"; }),
            depressionScreened: pncVisits.some(function (v) { return v.depression_screening_score !== null && v.depression_screening_score !== undefined; }),
        };
    }, [pncVisits]);

    return {
        /* Constants */
        PNC_VISIT_LABELS: PNC_VISIT_LABELS,
        UTERINE_INVOLUTION: UTERINE_INVOLUTION,
        LOCHIA_STATUS: LOCHIA_STATUS,
        PERINEUM_STATUS: PERINEUM_STATUS,
        BREAST_STATUS: BREAST_STATUS,
        BREASTFEEDING_STATUS: BREASTFEEDING_STATUS,
        PNC_COMPLICATION_OPTIONS: PNC_COMPLICATION_OPTIONS,
        EPDS_QUESTIONS: EPDS_QUESTIONS,
        /* Data */
        deliveryRecord: deliveryRecord,
        booking: booking,
        pncVisits: pncVisits,
        pncSchedule: pncSchedule,
        scheduleWithStatus: scheduleWithStatus,
        daysPostpartum: daysPostpartum,
        /* Form */
        form: form,
        setForm: setForm,
        handleChange: handleChange,
        toggleComplication: toggleComplication,
        editingVisit: editingVisit,
        /* EPDS */
        epdsResponses: epdsResponses,
        handleEpdsResponse: handleEpdsResponse,
        epdsScore: epdsScore,
        epdsComplete: epdsComplete,
        epdsInterpretation: epdsInterpretation,
        applyEpdsScore: applyEpdsScore,
        showEpds: showEpds,
        setShowEpds: setShowEpds,
        /* Computed */
        alerts: alerts,
        summary: summary,
        /* Actions */
        savePncVisit: savePncVisit,
        startEditVisit: startEditVisit,
        resetForm: resetForm,
        /* UI */
        loading: loading,
        saving: saving,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
    };
};
